import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { DollarSign, Clock, Award, ChevronDown, ChevronUp } from "lucide-react";

/**
 * Admin-side bid picker — collapsed by default, expands to show every bid
 * on a ticket. Admin taps "Pick" to accept a bid; that call unlocks the
 * winning vendor's timeline access + fires bid.accepted webhook.
 */
export default function AdminTicketBidsSection({ ticket, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accepting, setAccepting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/maintenance/${ticket.id}/bids`);
      setBids(data.items || []);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setLoading(false);
  }, [ticket.id]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const accept = async (bid) => {
    if (!window.confirm(`Assign ${bid.vendor_name} at $${Number(bid.amount).toFixed(2)}? They'll be notified and get timeline access; other bidders will see the ticket closed.`)) return;
    setAccepting(bid.id);
    try {
      await api.post(`/maintenance/${ticket.id}/bids/${bid.id}/accept`);
      toast.success(`${bid.vendor_name} assigned — pushed to their inbox`);
      await load();
      onUpdate?.();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setAccepting(null);
  };

  const winner = bids.find((b) => b.status === "accepted");
  const bidCount = ticket.bid_count ?? bids.length;

  return (
    <div className="mt-3 rounded-md border border-white/10 bg-white/[0.02]" data-testid={`admin-bids-${ticket.id}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs text-zinc-400 hover:text-zinc-200"
        data-testid={`admin-bids-toggle-${ticket.id}`}
      >
        <span className="flex items-center gap-2">
          <DollarSign className="h-3 w-3" />
          <span>Bids{bidCount ? ` (${bidCount})` : ""}</span>
          {ticket.assigned_vendor && (
            <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 bg-emerald-500/10 text-[10px] uppercase tracking-widest">
              <Award className="h-3 w-3 mr-1" /> {ticket.assigned_vendor}
            </Badge>
          )}
        </span>
        {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-2" data-testid={`admin-bids-list-${ticket.id}`}>
          {loading && <div className="text-xs text-zinc-500 py-2">Loading bids…</div>}
          {!loading && bids.length === 0 && (
            <div className="text-xs text-zinc-500 italic py-2" data-testid={`admin-bids-empty-${ticket.id}`}>
              No bids yet. Vendors on this property see the ticket in their portal.
            </div>
          )}
          {bids.map((b) => (
            <div
              key={b.id}
              className={
                "flex items-center justify-between rounded-md border p-3 " +
                (b.status === "accepted"
                  ? "border-emerald-500/40 bg-emerald-500/5"
                  : b.status === "declined"
                  ? "border-white/10 opacity-60"
                  : "border-white/10 bg-white/[0.02]")
              }
              data-testid={`admin-bid-${b.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-sm font-medium truncate">{b.vendor_name}</div>
                  {b.vendor_category && (
                    <Badge variant="outline" className="border-white/10 text-[10px] uppercase tracking-widest">
                      {b.vendor_category}
                    </Badge>
                  )}
                  <Badge
                    variant="outline"
                    className={
                      "text-[10px] uppercase tracking-widest " +
                      (b.status === "accepted"
                        ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
                        : b.status === "declined"
                        ? "border-zinc-500/40 text-zinc-500"
                        : "border-amber-500/40 text-amber-300 bg-amber-500/10")
                    }
                  >
                    {b.status}
                  </Badge>
                </div>
                {b.note && <div className="text-xs text-zinc-500 mt-1 truncate">{b.note}</div>}
                {b.eta_days !== null && b.eta_days !== undefined && (
                  <div className="text-[10px] text-zinc-500 flex items-center gap-1 mt-1">
                    <Clock className="h-3 w-3" /> ETA {b.eta_days} day{b.eta_days === 1 ? "" : "s"}
                  </div>
                )}
              </div>
              <div className="text-right ml-3 shrink-0">
                <div className="text-lg font-semibold tabular-nums">${Number(b.amount).toFixed(2)}</div>
                {!winner && b.status !== "accepted" && (
                  <Button
                    size="sm"
                    onClick={() => accept(b)}
                    disabled={accepting === b.id}
                    data-testid={`admin-bid-accept-${b.id}`}
                    className="mt-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white h-7 px-3 text-xs"
                  >
                    {accepting === b.id ? "…" : "Pick"}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
